import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Container,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import SuggestionChips from './SuggestionChips';

interface WelcomeScreenProps {
  userName?: string;
  onSuggestionClick?: (suggestion: string) => void;
  input: string;
  setInput: (input: string) => void;
  onSendMessage: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  isLoading: boolean;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  userName,
  onSuggestionClick,
  input,
  setInput,
  onSendMessage,
  onKeyPress,
  isLoading
}) => {
  const [expandedCategory, setExpandedCategory] = React.useState<string | null>(null);
  const inputAreaRef = React.useRef<HTMLDivElement>(null);

  const firstName = userName?.split(' ')[0];
  const capitalizedFirstName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : null;
  const greeting = capitalizedFirstName ? `How can I help you, ${capitalizedFirstName}?` : 'How can I help you?';

  // Handle click outside to reset expanded category
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputAreaRef.current && !inputAreaRef.current.contains(event.target as Node)) {
        setExpandedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'background.default'
      }}
    >
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        {/* Main heading with Otti icon */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              animation: 'fadeIn 0.5s ease-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(-10px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Box
              component="img"
              src="/otti-logo.png"
              alt="Otti Logo"
              sx={{
                width: 56,
                height: 56,
                filter: 'contrast(1.1) saturate(1.1)',
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 400,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
              }}
            >
              {greeting}
            </Typography>
          </Box>
        </Box>

        {/* Input Area */}
        <Box
          ref={inputAreaRef}
          sx={{
            maxWidth: 700,
            mx: 'auto',
            mt: 2,
            width: { xs: '90%', sm: '100%' },
            px: { xs: 2, sm: 3 },
            animation: 'slideUp 0.5s ease-out 0.1s both',
            '@keyframes slideUp': {
              '0%': {
                opacity: 0,
                transform: 'translateY(15px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              backgroundColor: 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#a6f0db',
                boxShadow: '0 2px 8px rgba(39, 196, 153, 0.08)',
              },
              '&:focus-within': {
                borderColor: '#a6f0db',
                boxShadow: '0 0 0 3px rgba(166, 240, 219, 0.2), 0 4px 12px rgba(39, 196, 153, 0.08)',
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={6}
                variant="standard"
                placeholder="Ask me about invoices, spend, and payments..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={onKeyPress}
                disabled={isLoading}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '40px',
                    '& textarea': {
                      resize: 'none',
                      padding: '8px 0',
                    },
                    '& input': {
                      padding: '8px 0',
                    },
                  },
                }}
              />
              <IconButton
                onClick={onSendMessage}
                disabled={!input.trim() || isLoading}
                sx={{
                  backgroundColor: input.trim() && !isLoading ? '#e8ecef' : '#f5f7fa',
                  color: input.trim() && !isLoading ? '#4a5568' : '#cbd5e0',
                  borderRadius: 1.5,
                  p: 1.75,
                  minWidth: '48px',
                  minHeight: '48px',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'scale(1)',
                  border: '1.5px solid transparent',
                  '&:hover': {
                    backgroundColor: input.trim() && !isLoading ? '#27c499' : '#f5f7fa',
                    color: input.trim() && !isLoading ? '#ffffff' : '#cbd5e0',
                    transform: input.trim() && !isLoading ? 'scale(1.08)' : 'scale(1)',
                    boxShadow: input.trim() && !isLoading ? '0 4px 16px rgba(39, 196, 153, 0.25)' : 'none',
                    borderColor: input.trim() && !isLoading ? '#27c499' : 'transparent',
                  },
                  '&:active': {
                    transform: input.trim() && !isLoading ? 'scale(0.95)' : 'scale(1)',
                  },
                  '&:disabled': {
                    backgroundColor: '#f5f7fa',
                    color: '#cbd5e0',
                    transform: 'scale(1)',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </IconButton>
            </Stack>
          </Paper>

          {/* Suggestion Chips - below the input Paper */}
          <SuggestionChips
            onPromptClick={setInput}
            isLoading={isLoading}
            expandedCategory={expandedCategory}
            setExpandedCategory={setExpandedCategory}
          />
        </Box>

      </Box>
    </Container>
  );
};

export default WelcomeScreen;